import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { referenceCodeTypeService } from '../../services/referenceCodeTypeService';
import { Eye, EyeOff, User, Mail, Lock, Phone, Building, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [loadingIndustryTypes, setLoadingIndustryTypes] = useState(true);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError
  } = useForm();

  const password = watch('password');

  useEffect(() => {
    loadIndustryTypes();
  }, []);

  const loadIndustryTypes = async () => {
    try {
      setLoadingIndustryTypes(true);
      const types = await referenceCodeTypeService.getAllActiveIndustryTypes();
      setIndustryTypes(types);
    } catch (error) {
      console.error('Error loading industry types:', error);
      toast.error('Failed to load industry types');
    } finally {
      setLoadingIndustryTypes(false);
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await signup(data);
      if (result.success) {
        toast.success('Account created successfully! Please sign in.');
        navigate('/login');
      } else {
        setError('root', { message: result.error });
        toast.error(result.error);
      }
    } catch (error) {
      setError('root', { message: 'An unexpected error occurred' });
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center mb-4">
            <span className="text-2xl font-bold text-white">W</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Create your account
          </h2>
          <p className="text-gray-600">
            Join WABot and start managing your WhatsApp Business API
          </p>
        </div>

        {/* Signup Form */}
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Name */}
                <div className="md:col-span-2">
                  <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="customerName"
                      type="text"
                      className={`input-field pl-10 ${errors.customerName ? 'input-error' : ''}`}
                      placeholder="Enter customer name"
                      {...register('customerName', {
                        required: 'Customer name is required',
                        minLength: {
                          value: 2,
                          message: 'Customer name must be at least 2 characters'
                        },
                        maxLength: {
                          value: 50,
                          message: 'Customer name must not exceed 50 characters'
                        }
                      })}
                    />
                  </div>
                  {errors.customerName && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.customerName.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="md:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      className={`input-field pl-10 ${errors.email ? 'input-error' : ''}`}
                      placeholder="Enter email address"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`input-field pl-10 pr-10 ${errors.password ? 'input-error' : ''}`}
                      placeholder="Enter password"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters'
                        }
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'input-error' : ''}`}
                      placeholder="Confirm password"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* Contact Phone */}
                <div>
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="contactPhone"
                      type="tel"
                      className={`input-field pl-10 ${errors.contactPhone ? 'input-error' : ''}`}
                      placeholder="+1234567890"
                      {...register('contactPhone', {
                        required: 'Contact phone is required',
                        pattern: {
                          value: /^\+?[1-9]\d{1,14}$/,
                          message: 'Invalid phone number format'
                        }
                      })}
                    />
                  </div>
                  {errors.contactPhone && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.contactPhone.message}
                    </p>
                  )}
                </div>

                {/* WABA Phone */}
                <div>
                  <label htmlFor="wabaPhone" className="block text-sm font-medium text-gray-700 mb-2">
                    WABA Phone
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="wabaPhone"
                      type="tel"
                      className={`input-field pl-10 ${errors.wabaPhone ? 'input-error' : ''}`}
                      placeholder="+1234567890"
                      {...register('wabaPhone', {
                        pattern: {
                          value: /^\+?[1-9]\d{1,14}$/,
                          message: 'Invalid phone number format'
                        }
                      })}
                    />
                  </div>
                  {errors.wabaPhone && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.wabaPhone.message}
                    </p>
                  )}
                </div>

                {/* WABA ID */}
                <div>
                  <label htmlFor="wabaId" className="block text-sm font-medium text-gray-700 mb-2">
                    WABA ID
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="wabaId"
                      type="text"
                      className={`input-field pl-10 ${errors.wabaId ? 'input-error' : ''}`}
                      placeholder="Enter WABA ID"
                      {...register('wabaId', {
                        minLength: {
                          value: 3,
                          message: 'WABA ID must be at least 3 characters'
                        },
                        maxLength: {
                          value: 50,
                          message: 'WABA ID must not exceed 50 characters'
                        },
                        pattern: {
                          value: /^[a-zA-Z0-9_-]+$/,
                          message: 'WABA ID can only contain letters, numbers, hyphens, and underscores'
                        }
                      })}
                    />
                  </div>
                  {errors.wabaId && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.wabaId.message}
                    </p>
                  )}
                </div>

                {/* Industry Type */}
                <div>
                  <label htmlFor="industryTypeCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Type
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="industryTypeCode"
                      className={`input-field pl-10 ${errors.industryTypeCode ? 'input-error' : ''}`}
                      disabled={loadingIndustryTypes}
                      {...register('industryTypeCode', {
                        required: 'Industry type is required'
                      })}
                    >
                      <option value="">
                        {loadingIndustryTypes ? 'Loading industry types...' : 'Select Industry Type'}
                      </option>
                      {industryTypes.map((type) => (
                        <option key={type.codeType} value={type.codeType}>
                          {type.codeValue} {type.description ? `(${type.description})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.industryTypeCode && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.industryTypeCode.message}
                    </p>
                  )}
                </div>

              </div>

              {/* Root Error */}
              {errors.root && (
                <div className="rounded-lg bg-error-50 border border-error-200 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-error-400" />
                    <div className="ml-3">
                      <p className="text-sm text-error-800">
                        {errors.root.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex justify-center items-center"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Create Account'
                )}
              </button>

              {/* Login Link */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 mt-8">
          <p>&copy; 2024 WABot. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
